import {
  RbacResourceType,
  RbacRole,
  RoleAccess,
  Organization,
} from '@cast/types';

import { AccessVia, ControlDataBySubjectId } from './types';

export type ResourceKey = `${RbacResourceType}:${string}`;

export type ResourceAccessEntry = {
  id: string;
  type: RbacResourceType;
  access: RoleAccess;
  accessVia: AccessVia;
  hasAccessTo: string[];
};

/**
 * Get permission groups from a role by ID.
 * Returns the permission endpoints that are allowed for the given role.
 */
export const getPermissionGroups = (
  rolesById: Record<string, RbacRole>,
  roleId?: string
): string[] => {
  if (!roleId) return [];
  return (
    rolesById[roleId]?.definition?.permissions?.find(
      (p) => p.effect === 'ALLOW'
    )?.groups ?? []
  );
};

/**
 * Determine how access was granted (direct, inherited, or via groups).
 * Returns the accessVia value based on the access properties.
 */
export const getAccessVia = (access: RoleAccess): AccessVia => {
  if (access.groupsRef?.length) {
    return access.groupsRef;
  }
  if (access.inherited) {
    return 'inherited';
  }
  return 'direct';
};

/**
 * Apply organization-to-cluster access inheritance.
 * For each subject, if their organization-level access is higher than cluster-level,
 * their clusters inherit the higher organization access.
 * Preserves existing groupsRef on cluster access.
 */
export const applyOrgToClusterInheritance = (
  subjectAccessMap: Map<string, Map<ResourceKey, ResourceAccessEntry>>
): void => {
  subjectAccessMap.forEach((resourceMap) => {
    // Find highest org-level access
    let highestOrgAccess: ResourceAccessEntry | undefined;

    resourceMap.forEach((entry) => {
      if (entry.type === 'ORGANIZATION') {
        if (
          !highestOrgAccess ||
          (entry.access.roleLevel || 0) >
            (highestOrgAccess.access.roleLevel || 0)
        ) {
          highestOrgAccess = entry;
        }
      }
    });

    if (!highestOrgAccess) return;

    // Apply inheritance to cluster resources
    resourceMap.forEach((entry, key) => {
      if (entry.type === 'CLUSTER') {
        const clusterAccess = entry.access;
        if (
          (highestOrgAccess!.access.roleLevel || 0) >
          (clusterAccess.roleLevel || 0)
        ) {
          const inheritedAccess: RoleAccess = {
            ...highestOrgAccess!.access,
            inherited: true,
            groupsRef: clusterAccess.groupsRef, // Preserve existing groupsRef
          };

          resourceMap.set(key, {
            ...entry,
            access: inheritedAccess,
            accessVia: getAccessVia(inheritedAccess),
            hasAccessTo: highestOrgAccess!.hasAccessTo,
          });
        }
      }
    });
  });
};

/**
 * Apply parent organization access to child organizations.
 * When a user has a role on a parent (enterprise) organization with higher
 * permissions than their child org role, the parent permissions cascade down.
 */
export const applyParentOrgToChildOrgInheritance = (
  subjectAccessMap: Map<string, Map<ResourceKey, ResourceAccessEntry>>,
  organizations: Organization[]
): void => {
  // Build organization lookup and parent-child relationship maps
  const orgMap = new Map(organizations.map((org) => [org.id, org]));
  const childrenByParent = new Map<string, Organization[]>();

  organizations.forEach((org) => {
    if (org.parentId) {
      if (!childrenByParent.has(org.parentId)) {
        childrenByParent.set(org.parentId, []);
      }
      childrenByParent.get(org.parentId)!.push(org);
    }
  });

  // For each subject
  subjectAccessMap.forEach((resourceMap) => {
    // Find parent organization access entries
    const parentOrgAccess = new Map<string, ResourceAccessEntry>();

    resourceMap.forEach((entry) => {
      if (entry.type === 'ORGANIZATION') {
        const org = orgMap.get(entry.id);
        // If this is a parent (enterprise) organization (no parentId)
        if (org && !org.parentId) {
          parentOrgAccess.set(entry.id, entry);
        }
      }
    });

    // Apply parent roles to children
    parentOrgAccess.forEach((parentEntry, parentId) => {
      const children = childrenByParent.get(parentId) || [];

      children.forEach((childOrg) => {
        const childKey: ResourceKey = `ORGANIZATION:${childOrg.id}`;
        const childEntry = resourceMap.get(childKey);

        // If child has lower role level or no role, inherit from parent
        if (
          !childEntry ||
          (parentEntry.access.roleLevel || 0) >
            (childEntry.access.roleLevel || 0)
        ) {
          const inheritedAccess: ResourceAccessEntry = {
            ...parentEntry,
            id: childOrg.id,
            access: {
              ...parentEntry.access,
              inherited: true,
              // Preserve existing groupsRef from child if it exists
              groupsRef: childEntry?.access.groupsRef,
            },
            accessVia: getAccessVia({
              ...parentEntry.access,
              inherited: true,
              groupsRef: childEntry?.access.groupsRef,
            }),
          };

          resourceMap.set(childKey, inheritedAccess);
        }
      });
    });
  });
};

/**
 * Convert internal Map structure to final ControlDataBySubjectId format.
 * Groups resources by subject ID, separating clusters and organizations.
 */
export const convertAccessMapToControlData = (
  subjectAccessMap: Map<string, Map<ResourceKey, ResourceAccessEntry>>
): ControlDataBySubjectId => {
  const result: ControlDataBySubjectId = {};

  subjectAccessMap.forEach((resourceMap, subjectId) => {
    const controlData = {
      clusters: {} as Record<string, ResourceAccessEntry>,
      organizations: {} as Record<string, ResourceAccessEntry>,
    };

    resourceMap.forEach((entry) => {
      if (entry.type === 'CLUSTER') {
        controlData.clusters[entry.id] = {
          id: entry.id,
          type: entry.type,
          access: entry.access,
          accessVia: entry.accessVia,
          hasAccessTo: entry.hasAccessTo,
        };
      } else if (entry.type === 'ORGANIZATION') {
        controlData.organizations[entry.id] = {
          id: entry.id,
          type: entry.type,
          access: entry.access,
          accessVia: entry.accessVia,
          hasAccessTo: entry.hasAccessTo,
        };
      }
    });

    result[subjectId] = controlData;
  });

  return result;
};
